/* ==========================================================================
   t26-os.js — interactions for the OurSphere app page.
   Loaded after t26.js. Independent of it: t26.js still owns reveal, scrub,
   sticky steps and count-up. This file owns only the four new widgets.
     1. Segmented screen switcher   [data-os-switch]   (retired, kept harmless)
     2. Four-corner safe-zone demo  [data-os-zone]
     3. Tracking-mode selector      [data-os-modes]
     4. Release-notes accordion     [data-os-rel]
   Every widget is progressive: the markup renders and reads correctly with
   JavaScript switched off, and each init is wrapped so one failure cannot
   take the others down.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__t26OsInit) return;
  window.__t26OsInit = true;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function safe(name, fn) {
    try { fn(); } catch (e) { if (window.console) console.warn('[t26-os] ' + name + ' failed', e); }
  }

  ready(function () {

    /* ------------------------------------------------ 1. SCREEN SWITCHER */

    safe('switch', function () {
      document.querySelectorAll('[data-os-switch]').forEach(function (root) {
        var tabs = [].slice.call(root.querySelectorAll('[role="tab"]'));
        var panels = [].slice.call(root.querySelectorAll('[data-os-panel]'));
        var shot = root.querySelector('[data-os-shot]');
        if (!tabs.length) return;

        function select(i) {
          tabs.forEach(function (t, n) {
            t.setAttribute('aria-selected', n === i ? 'true' : 'false');
            t.tabIndex = n === i ? 0 : -1;
          });
          panels.forEach(function (p, n) { p.hidden = n !== i; });
          var src = tabs[i].getAttribute('data-os-src');
          if (shot && src && shot.getAttribute('src') !== src) {
            shot.style.opacity = '0';
            setTimeout(function () {
              shot.setAttribute('src', src);
              shot.setAttribute('alt', tabs[i].getAttribute('data-os-alt') || '');
              shot.style.opacity = '1';
            }, reduce ? 0 : 180);
          }
        }

        tabs.forEach(function (t, i) {
          t.addEventListener('click', function () { select(i); });
          t.addEventListener('keydown', function (e) {
            var d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
            if (!d) return;
            e.preventDefault();
            var n = (i + d + tabs.length) % tabs.length;
            tabs[n].focus();
            select(n);
          });
        });
        select(0);
      });
    });

    /* ------------------------------------------ 2. FOUR-CORNER SAFE ZONE */

    safe('zone', function () {
      document.querySelectorAll('[data-os-zone]').forEach(function (root) {
        var map = root.querySelector('.os-map');
        var poly = root.querySelector('[data-os-shape]');
        var handles = [].slice.call(root.querySelectorAll('[data-os-corner]'));
        var label = root.querySelector('[data-os-label]');
        var pin = root.querySelector('[data-os-walker]');
        var feed = root.querySelector('[data-os-feed]');
        var empty = root.querySelector('[data-os-empty]');
        var read = root.querySelector('[data-os-read]');
        if (!map || !poly || !pin || !feed) return;

        /* FOUR CORNERS, clockwise from top left, held as percentages of the map
           box rather than pixels. Two reasons. The shape then survives any
           resize with no recalculation, and a point-in-polygon test in
           percentage space returns exactly what one in pixel space would: the
           two spaces differ by an affine scale, and affine transforms preserve
           inside and outside. */
        var pts = [[24, 22], [63, 19], [69, 60], [26, 57]];

        var M_WIDE = 800, M_TALL = 600;  /* the whole map reads as 800m x 600m */
        var PAD = 3;                     /* keep corners off the map edge */

        var name = root.getAttribute('data-os-name') || 'Buddy';
        var zoneName = root.getAttribute('data-os-zonename') || 'Home';

        /* Alert wording comes from the section settings so it can be kept in
           step with OurSphere without editing this file. The app says "has
           entered" and "has left"; these are the fallbacks if the attributes
           are ever missing. */
        var verbEnter = root.getAttribute('data-os-enter') || 'has entered';
        var verbLeave = root.getAttribute('data-os-leave') || 'has left';

        var path = [
          [20, 78], [26, 62], [34, 52], [44, 44], [55, 40],
          [66, 38], [76, 44], [82, 58], [74, 70], [60, 76],
          [46, 80], [32, 82]
        ];
        var step = 0;
        var wasInside = null;

        function clone(p) { return p.map(function (q) { return [q[0], q[1]]; }); }

        function centroid(p) {
          var x = 0, y = 0;
          p.forEach(function (q) { x += q[0]; y += q[1]; });
          return [x / p.length, y / p.length];
        }

        /* Ray casting. Counts how many edges a ray from the point crosses:
           odd means inside. Works on any simple polygon, so it keeps giving the
           right answer whatever shape the corners are dragged into. */
        function inside(pt) {
          var x = pt[0], y = pt[1], hit = false;
          for (var i = 0, j = pts.length - 1; i < pts.length; j = i++) {
            var xi = pts[i][0], yi = pts[i][1], xj = pts[j][0], yj = pts[j][1];
            if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) hit = !hit;
          }
          return hit;
        }

        /* A quad with freely draggable corners can be folded into a bow tie,
           which looks broken and makes the alert feed nonsense. Opposite edges
           crossing is the only way that happens with four points, so a move
           that would cross them is simply rejected. */
        function cross(o, p, q) {
          return (p[0] - o[0]) * (q[1] - o[1]) - (p[1] - o[1]) * (q[0] - o[0]);
        }
        function segmentsCross(a, b, c, d) {
          var d1 = cross(c, d, a), d2 = cross(c, d, b);
          var d3 = cross(a, b, c), d4 = cross(a, b, d);
          return ((d1 > 0) !== (d2 > 0)) && ((d3 > 0) !== (d4 > 0));
        }
        function simple(p) {
          return !segmentsCross(p[0], p[1], p[2], p[3]) &&
                 !segmentsCross(p[1], p[2], p[3], p[0]);
        }

        function commit(next) {
          next.forEach(function (q) {
            q[0] = Math.max(PAD, Math.min(100 - PAD, q[0]));
            q[1] = Math.max(8, Math.min(92, q[1]));
          });
          if (!simple(next)) return false;
          pts = next;
          draw();
          recheck();
          return true;
        }

        function draw() {
          /* Clamp here as well as on interaction: a viewport resize can leave a
             zone that was legal at desktop width hanging over the edge of a
             narrower map. */
          pts.forEach(function (q) {
            q[0] = Math.max(PAD, Math.min(100 - PAD, q[0]));
            q[1] = Math.max(8, Math.min(92, q[1]));
          });

          poly.setAttribute('points', pts.map(function (p) {
            return p[0].toFixed(2) + ',' + p[1].toFixed(2);
          }).join(' '));

          handles.forEach(function (h, i) {
            h.style.left = pts[i][0] + '%';
            h.style.top = pts[i][1] + '%';
          });

          var xs = pts.map(function (p) { return p[0]; });
          var ys = pts.map(function (p) { return p[1]; });
          var w = Math.round((Math.max.apply(null, xs) - Math.min.apply(null, xs)) / 100 * M_WIDE / 10) * 10;
          var h = Math.round((Math.max.apply(null, ys) - Math.min.apply(null, ys)) / 100 * M_TALL / 10) * 10;

          var c = centroid(pts);
          if (label) {
            label.style.left = c[0] + '%';
            label.style.top = c[1] + '%';
            label.textContent = zoneName;
          }
          if (read) read.innerHTML = 'About <b>' + w + ' m</b> by <b>' + h + ' m</b>';
        }

        function stamp() {
          var d = new Date();
          var hh = d.getHours(), mm = d.getMinutes();
          var ap = hh >= 12 ? 'PM' : 'AM';
          hh = hh % 12; if (hh === 0) hh = 12;
          return hh + ':' + (mm < 10 ? '0' : '') + mm + ' ' + ap;
        }

        var ICON_IN = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12l6 6L20 6"/></svg>';
        var ICON_OUT = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 8v5M12 17h.01"/><path d="M10.3 3.9 2.6 17.4A2 2 0 0 0 4.3 20.4h15.4a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/></svg>';

        function log(entered) {
          if (empty) empty.hidden = true;
          var li = document.createElement('li');
          li.innerHTML =
            '<span class="os-feed__ico os-feed__ico--' + (entered ? 'in' : 'out') + '">' +
            (entered ? ICON_IN : ICON_OUT) + '</span>' +
            '<span><b>' + name + '</b> ' + (entered ? verbEnter : verbLeave) + ' ' + zoneName +
            '<time>' + stamp() + '</time></span>';
          feed.insertBefore(li, feed.firstChild);
          while (feed.children.length > 5) feed.removeChild(feed.lastChild);
        }

        function place() {
          var p = path[step];
          pin.style.left = p[0] + '%';
          pin.style.top = p[1] + '%';
          var now = inside(p);
          if (wasInside !== null && now !== wasInside) log(now);
          wasInside = now;
        }

        /* Dragging the boundary over or away from the walker should fire the
           same alert a real crossing would, or the demo feels dead while you
           are the one doing the moving. */
        function recheck() {
          var now = inside(path[step]);
          if (wasInside !== null && now !== wasInside) log(now);
          wasInside = now;
        }

        /* ---------------- pointer: move the whole zone, or one corner ------- */

        var mode = null, corner = -1, grabbed = null, origin = null;

        function pct(e) {
          var b = map.getBoundingClientRect();
          return [(e.clientX - b.left) / b.width * 100, (e.clientY - b.top) / b.height * 100];
        }

        function start(e, m, i) {
          mode = m;
          corner = i;
          grabbed = clone(pts);
          origin = pct(e);
          map.classList.add('is-dragging');
          if (map.setPointerCapture) { try { map.setPointerCapture(e.pointerId); } catch (err) {} }
          e.preventDefault();
        }

        poly.addEventListener('pointerdown', function (e) { start(e, 'move', -1); });
        handles.forEach(function (h, i) {
          h.addEventListener('pointerdown', function (e) { e.stopPropagation(); start(e, 'corner', i); });
        });

        map.addEventListener('pointermove', function (e) {
          if (!mode) return;
          var now = pct(e);
          var dx = now[0] - origin[0], dy = now[1] - origin[1];
          var next = clone(grabbed);
          if (mode === 'move') {
            next.forEach(function (q) { q[0] += dx; q[1] += dy; });
          } else {
            next[corner][0] += dx;
            next[corner][1] += dy;
          }
          commit(next);
        });

        function end(e) {
          if (!mode) return;
          mode = null;
          corner = -1;
          map.classList.remove('is-dragging');
          if (map.releasePointerCapture && e && e.pointerId != null) {
            try { map.releasePointerCapture(e.pointerId); } catch (err) {}
          }
        }
        map.addEventListener('pointerup', end);
        map.addEventListener('pointercancel', end);
        map.addEventListener('pointerleave', end);

        /* ---------------- keyboard and the grow / shrink buttons ------------ */

        var ARROWS = { ArrowLeft: [-2, 0], ArrowRight: [2, 0], ArrowUp: [0, -2], ArrowDown: [0, 2] };

        poly.addEventListener('keydown', function (e) {
          var d = ARROWS[e.key];
          if (!d) return;
          e.preventDefault();
          var next = clone(pts);
          next.forEach(function (q) { q[0] += d[0]; q[1] += d[1]; });
          commit(next);
        });

        handles.forEach(function (h, i) {
          h.addEventListener('keydown', function (e) {
            var d = ARROWS[e.key];
            if (!d) return;
            e.preventDefault();
            var next = clone(pts);
            next[i][0] += d[0];
            next[i][1] += d[1];
            commit(next);
          });
        });

        /* Scale about the centroid so the zone grows and shrinks in place
           rather than drifting towards a corner. */
        root.querySelectorAll('[data-os-scale]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var k = parseFloat(btn.getAttribute('data-os-scale'));
            var c = centroid(pts);
            var next = clone(pts);
            next.forEach(function (q) {
              q[0] = c[0] + (q[0] - c[0]) * k;
              q[1] = c[1] + (q[1] - c[1]) * k;
            });
            commit(next);
          });
        });

        window.addEventListener('resize', draw, { passive: true });

        draw();
        place();

        if (!reduce) {
          var timer = null;
          var io = new IntersectionObserver(function (entries) {
            entries.forEach(function (en) {
              if (en.isIntersecting && !timer) {
                timer = setInterval(function () {
                  step = (step + 1) % path.length;
                  place();
                }, 1400);
              } else if (!en.isIntersecting && timer) {
                clearInterval(timer); timer = null;
              }
            });
          }, { threshold: 0.25 });
          io.observe(root);
        }
      });
    });

    /* ------------------------------------------------ 3. MODE SELECTOR */

    safe('modes', function () {
      document.querySelectorAll('[data-os-modes]').forEach(function (root) {
        var btns = [].slice.call(root.querySelectorAll('[data-os-mode]'));
        var pulse = root.querySelector('[data-os-pulse]');
        /* NOT [data-os-cap]: the mode BUTTONS carry data-os-cap as their source
           value, so that selector matched the first button and writing
           textContent to it erased its label. The plate has its own hook. */
        var cap = root.querySelector('[data-os-caption-out]');
        var shot = root.querySelector('[data-os-modeshot]');
        if (!btns.length) return;

        function select(i) {
          btns.forEach(function (b, n) { b.setAttribute('aria-pressed', n === i ? 'true' : 'false'); });
          var b = btns[i];
          var rate = b.getAttribute('data-os-rate') || '3s';
          var emg = b.getAttribute('data-os-mode') === 'emergency';
          if (pulse) {
            pulse.style.setProperty('--os-rate', rate);
            pulse.classList.toggle('os-pulse--emg', emg);
          }
          if (cap) cap.textContent = b.getAttribute('data-os-cap') || '';
          var src = b.getAttribute('data-os-src');
          if (shot && src && shot.getAttribute('src') !== src) {
            shot.style.opacity = '0';
            setTimeout(function () { shot.setAttribute('src', src); shot.style.opacity = '1'; }, reduce ? 0 : 180);
          }
        }

        btns.forEach(function (b, i) { b.addEventListener('click', function () { select(i); }); });
        select(0);
      });
    });

    /* --------------------------------------------- 4. RELEASE ACCORDION */

    safe('releases', function () {
      document.querySelectorAll('[data-os-rel]').forEach(function (root) {
        var btns = [].slice.call(root.querySelectorAll('.os-rel__btn'));
        btns.forEach(function (b, i) {
          b.setAttribute('aria-expanded', i === 0 ? 'true' : 'false');
          b.addEventListener('click', function () {
            var open = b.getAttribute('aria-expanded') === 'true';
            b.setAttribute('aria-expanded', open ? 'false' : 'true');
          });
        });
      });
    });

  });
})();
